<script setup>
import { computed, ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'

const asset = (path) => `${import.meta.env.BASE_URL}${path}`

const swiperInstance = ref(null)
const canSlidePrev = ref(false)
const canSlideNext = ref(true)

const bigCard = {
  image: 'top-live-casino/big-card.png',
  name: 'Big Game',
}

const liveGamesBase = [
  { image: 'games/game-1.png', name: 'Royal Joker' },
  { image: 'games/game-2.png', name: 'Rise of Olympus 100' },
  { image: 'games/game-3.png', name: 'Lucky Penny' },
  { image: 'games/game-4.png', name: 'Cherry Max 7s' },
  { image: 'games/game-5.png', name: 'Wolf Fang Ice & Fire' },
]

const liveGames = Array.from({ length: 4 }, (_, round) =>
  liveGamesBase.map((game) => ({
    ...game,
    id: `${round}-${game.name}`,
  })),
).flat()

const liveGameColumns = computed(() => {
  const columns = []

  for (let i = 0; i < liveGames.length; i += 2) {
    columns.push(liveGames.slice(i, i + 2))
  }

  return columns
})

function setSwiper(swiper) {
  swiperInstance.value = swiper
  updateNavigation(swiper)
}

function updateNavigation(swiper = swiperInstance.value) {
  if (!swiper) return

  canSlidePrev.value = !swiper.isBeginning
  canSlideNext.value = !swiper.isEnd
}

function slidePrev() {
  swiperInstance.value?.slidePrev()
}

function slideNext() {
  swiperInstance.value?.slideNext()
}
</script>

<template>
  <section class="top-live-casino" aria-labelledby="top-live-casino-title">
    <div class="top-live-casino__inner">
      <div class="top-live-casino__header">
        <h2 id="top-live-casino-title" class="top-live-casino__title">
          <img :src="asset('top-live-casino/top-live-casino.svg')" alt="" aria-hidden="true" />
          <span>Top Live Casino</span>
        </h2>

        <div class="top-live-casino__actions">
          <button
            class="top-live-casino__arrow"
            type="button"
            aria-label="Previous live casino games"
            :disabled="!canSlidePrev"
            @click="slidePrev"
          >
            <span class="top-live-casino__arrow-icon top-live-casino__arrow-icon--prev" aria-hidden="true"></span>
          </button>

          <button
            class="top-live-casino__arrow"
            type="button"
            aria-label="Next live casino games"
            :disabled="!canSlideNext"
            @click="slideNext"
          >
            <span class="top-live-casino__arrow-icon top-live-casino__arrow-icon--next" aria-hidden="true"></span>
          </button>

          <button class="top-live-casino__see-all" type="button">See All</button>
        </div>
      </div>

      <div
        class="top-live-casino__slider-frame app-swiper-fade"
        :class="{
          'app-swiper-fade--left': canSlidePrev,
          'app-swiper-fade--right': canSlideNext,
        }"
      >
        <Swiper
          class="top-live-casino__slider"
          :slides-per-view="'auto'"
          :space-between="14"
          :breakpoints="{
            768: { spaceBetween: 18 },
          }"
          @swiper="setSwiper"
          @slideChange="updateNavigation"
          @resize="updateNavigation"
        >
          <SwiperSlide class="top-live-casino__slide top-live-casino__slide--big">
            <article class="top-live-casino__big-card">
              <img :src="asset(bigCard.image)" :alt="bigCard.name" />
            </article>
          </SwiperSlide>

          <SwiperSlide
            v-for="(column, columnIndex) in liveGameColumns"
            :key="columnIndex"
            class="top-live-casino__slide top-live-casino__slide--column"
          >
            <article
              v-for="game in column"
              :key="game.id"
              class="top-live-casino__game-card"
            >
              <img :src="asset(game.image)" :alt="game.name" />
            </article>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<style scoped>
.top-live-casino {
  width: 100%;
}

.top-live-casino__inner {
  --top-live-gap: 14px;
  --top-live-card-width: clamp(116px, 33vw, 138px);
  --top-live-big-width: clamp(196px, 56vw, 240px);

  width: min(100%, 1280px);
  margin: 0 auto;
  padding: 10px 16px 44px;
  overflow: hidden;
}

.top-live-casino__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.top-live-casino__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.top-live-casino__title img {
  width: 25px;
  height: 25px;
  object-fit: contain;
}

.top-live-casino__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-live-casino__arrow,
.top-live-casino__see-all {
  appearance: none;
  border: 0;
  border-radius: 5px;
  color: #fff;
}

.top-live-casino__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid transparent;
  background:
    var(--gradient-background) padding-box,
    var(--gradient-border) border-box;
  line-height: 0;
  transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  vertical-align: middle;
}

.top-live-casino__arrow:disabled {
  cursor: default;
  opacity: 0.55;
}

.top-live-casino__arrow-icon {
  display: block;
  width: 13px;
  height: 13px;
  border-top: 5px solid currentColor;
  border-right: 5px solid currentColor;
}

.top-live-casino__arrow-icon--prev {
  transform: translateX(2px) rotate(-135deg);
}

.top-live-casino__arrow-icon--next {
  transform: translateX(-2px) rotate(45deg);
}

.top-live-casino__see-all {
  min-width: 96px;
  height: 42px;
  padding: 0 18px;
  background: var(--color-accent);
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.top-live-casino__arrow:not(:disabled):hover {
  background:
    var(--gradient-background-hover) padding-box,
    var(--gradient-border-hover) border-box;
  box-shadow: var(--shadow-control-hover);
}

.top-live-casino__see-all:hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-accent-hover);
}

.top-live-casino__slider {
  overflow: visible;
}

.top-live-casino__slider-frame {
  --app-swiper-fade-offset: 16px;
}

.top-live-casino__slide {
  width: var(--top-live-card-width);
  height: auto;
}

.top-live-casino__slide--big {
  width: var(--top-live-big-width);
}

.top-live-casino__slide--column {
  display: flex;
  flex-direction: column;
  gap: var(--top-live-gap);
}

.top-live-casino__big-card,
.top-live-casino__game-card {
  overflow: hidden;
  width: 100%;
  border-radius: 6px;
  background: #071536;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.top-live-casino__big-card:hover,
.top-live-casino__game-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.22);
}

.top-live-casino__big-card {
  aspect-ratio: 273 / 434;
}

.top-live-casino__game-card {
  aspect-ratio: 153 / 207;
  border: 1px solid rgb(255 211 52 / 0.8);
}

.top-live-casino__big-card img,
.top-live-casino__game-card img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 640px) {
  .top-live-casino__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .top-live-casino__actions {
    width: 100%;
  }

  .top-live-casino__see-all {
    margin-left: auto;
  }
}

@media (min-width: 768px) {
  .top-live-casino__inner {
    --top-live-gap: 18px;
    --top-live-card-width: clamp(112px, 14vw, 150px);
    --top-live-big-width: clamp(220px, 24vw, 268px);

    padding: 18px 48px 56px;
  }

  .top-live-casino__header {
    margin-bottom: 24px;
  }

  .top-live-casino__title {
    font-size: 1.5rem;
  }

  .top-live-casino__title img {
    width: 30px;
    height: 30px;
  }

  .top-live-casino__arrow {
    width: 44px;
    height: 44px;
  }

  .top-live-casino__see-all {
    min-width: 110px;
    height: 44px;
  }

  .top-live-casino__slider-frame {
    --app-swiper-fade-offset: 48px;
  }

}
</style>
